import ComponentPreview from "../builder/ComponentPreview";
import { createComponentFromTemplate } from "../../lib/component-tree";

/** Callbacks usados apenas quando o container é renderizado dentro do builder. */
export interface ContainerBuilderHandlers {
  isBuilder?: boolean;
  onEdit?: (component: any) => void;
  onDelete?: (componentId: string) => void;
  onDataEditClick?: (componentId: string) => void;
  onColumnsEditClick?: (componentId: string) => void;
  onChartFieldsEditClick?: (componentId: string) => void;
  onKanbanColumnsEditClick?: (componentId: string) => void;
  onAddChild?: (containerId: string, child: any) => void;
  onMoveComponent?: (componentId: string, targetContainerId: string) => void;
}

interface ContainerWidgetProps {
  component: any;
  allComponents?: any[];
  onButtonClick?: (componentId: string) => void;
  onComponentChange?: (componentId: string, newConfig: any) => void;
  activeFilters?: Record<string, string>;
  onFilterChange?: (field: string, value: string) => void;
  builder?: ContainerBuilderHandlers;
}

/**
 * Container/Seção: agrupa outros componentes numa caixa de layout flex.
 * - Direção configurável (vertical/horizontal); filhos preenchem o espaço (flex: 1).
 * - Aceita componentes arrastados da paleta (criar) e componentes existentes (mover).
 *
 * Cada filho é renderizado pelo MESMO ComponentPreview usado pelos componentes de
 * topo, então herda exatamente a mesma toolbar e o mesmo respeito às permissões
 * (os callbacks chegam já filtrados de quem renderiza o container). Usamos
 * `suppressDragHandle` para não criar uma `.drag-handle` que o grid externo
 * confundiria com o arraste do próprio container.
 */
export default function ContainerWidget({
  component,
  allComponents = [],
  onButtonClick,
  onComponentChange,
  activeFilters,
  onFilterChange,
  builder,
}: ContainerWidgetProps) {
  const config = component.config || {};
  const children: any[] = config.children || [];
  const isHorizontal = config.direction === "horizontal";
  const style = config.style || {};
  const isBuilder = !!builder?.isBuilder;

  const handleDragOver = (e: React.DragEvent) => {
    if (!isBuilder) return;
    // preventDefault habilita o drop. Fazemos sempre no builder; o handleDrop
    // ignora qualquer coisa que não seja um componente.
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!isBuilder) return;

    // 1) Componente que já existe na página sendo movido para dentro daqui.
    const movingId = e.dataTransfer.getData("application/x-component-id");
    if (movingId) {
      e.preventDefault();
      e.stopPropagation();
      if (movingId !== component.id) {
        builder?.onMoveComponent?.(movingId, component.id);
      }
      return;
    }

    // 2) Novo componente vindo da paleta.
    const templateId = e.dataTransfer.getData("application/x-component-template");
    if (!templateId || !builder?.onAddChild) return;
    e.preventDefault();
    e.stopPropagation();
    const child = createComponentFromTemplate(templateId);
    // Sem aninhar containers dentro de containers nesta versão.
    if (!child || child.type === "container") return;
    builder.onAddChild(component.id, child);
  };

  return (
    <div
      className="h-full flex flex-col rounded-lg border border-border text-card-foreground overflow-hidden"
      style={{
        // Cor padrão dos componentes (--card) escurecida um pouco, para dar
        // profundidade: os componentes internos (bg-card) "saltam" sobre o fundo.
        backgroundColor: "color-mix(in srgb, hsl(var(--card)) 72%, #000)",
      }}
    >
      {/* Cabeçalho (também serve de alça de arraste do container no grid) */}
      {(isBuilder || config.title) && (
        <div
          className={`flex items-center gap-2 px-3 py-2 flex-shrink-0 ${
            isBuilder ? "drag-handle cursor-move border-b bg-muted/40" : ""
          }`}
        >
          {config.title ? (
            <span className="text-sm font-semibold truncate">
              {config.title}
            </span>
          ) : isBuilder ? (
            <span className="text-xs text-muted-foreground">
              🗂️ Container ({children.length}) —{" "}
              {isHorizontal ? "horizontal" : "vertical"}
            </span>
          ) : null}
        </div>
      )}

      {/* Corpo flex com os filhos */}
      <div
        className="flex-1 flex min-h-0 overflow-auto"
        style={{
          flexDirection: isHorizontal ? "row" : "column",
          gap: config.gap ?? 16,
          padding: style.padding ?? 16,
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {children.length === 0 ? (
          <div className="flex-1 flex items-center justify-center border-2 border-dashed rounded-md text-muted-foreground text-sm min-h-[120px] text-center px-4">
            {isBuilder
              ? "Arraste componentes da paleta para dentro deste container"
              : ""}
          </div>
        ) : (
          children.map((child) =>
            isBuilder ? (
              <div key={child.id} className="flex-1 min-w-0 min-h-[120px]">
                <ComponentPreview
                  component={child}
                  isBuilder={true}
                  suppressDragHandle
                  onEdit={builder?.onEdit}
                  onDelete={builder?.onDelete}
                  onDataEditClick={builder?.onDataEditClick}
                  onColumnsEditClick={builder?.onColumnsEditClick}
                  onChartFieldsEditClick={builder?.onChartFieldsEditClick}
                  onKanbanColumnsEditClick={builder?.onKanbanColumnsEditClick}
                  onComponentChange={onComponentChange}
                  onButtonClick={onButtonClick}
                  onMoveComponent={builder?.onMoveComponent}
                  activeFilters={activeFilters}
                  onFilterChange={onFilterChange}
                  allComponents={allComponents}
                />
              </div>
            ) : (
              <div key={child.id} className="flex-1 min-w-0 min-h-[120px]">
                <ComponentPreview
                  component={child}
                  onEdit={builder?.onEdit}
                  onDataEditClick={builder?.onDataEditClick}
                  onColumnsEditClick={builder?.onColumnsEditClick}
                  onChartFieldsEditClick={builder?.onChartFieldsEditClick}
                  onComponentChange={onComponentChange}
                  onButtonClick={onButtonClick}
                  activeFilters={activeFilters}
                  onFilterChange={onFilterChange}
                  allComponents={allComponents}
                />
              </div>
            )
          )
        )}
      </div>
    </div>
  );
}
